import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { trackFormSubmit } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    employee_count: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, employee_count: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.company || !formData.employee_count) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/demo-requests`, formData);
      trackFormSubmit();
      toast.success('Demande envoyée avec succès ! Nous vous recontactons sous 24h.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        employee_count: '',
      });
    } catch (error) {
      console.error('Error submitting demo request:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 md:p-8 max-w-lg mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="demo-form">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#f5f5f5] text-sm font-medium">
            Nom complet
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Jean Dupont"
            className="bg-[#09090b] border-white/10 text-[#f5f5f5] placeholder:text-[#71717a] focus:border-[#3b82f6] focus:ring-[#3b82f6]"
            data-testid="demo-form-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#f5f5f5] text-sm font-medium">
            Email professionnel
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="jean@entreprise.fr"
            className="bg-[#09090b] border-white/10 text-[#f5f5f5] placeholder:text-[#71717a] focus:border-[#3b82f6] focus:ring-[#3b82f6]"
            data-testid="demo-form-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#f5f5f5] text-sm font-medium">
            Téléphone
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+33 6 12 34 56 78"
            className="bg-[#09090b] border-white/10 text-[#f5f5f5] placeholder:text-[#71717a] focus:border-[#3b82f6] focus:ring-[#3b82f6]"
            data-testid="demo-form-phone"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-[#f5f5f5] text-sm font-medium">
            Nom de l'entreprise
          </Label>
          <Input
            id="company"
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            placeholder="Acme Corp"
            className="bg-[#09090b] border-white/10 text-[#f5f5f5] placeholder:text-[#71717a] focus:border-[#3b82f6] focus:ring-[#3b82f6]"
            data-testid="demo-form-company"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="employee_count" className="text-[#f5f5f5] text-sm font-medium">
            Nombre de collaborateurs
          </Label>
          <Select onValueChange={handleSelectChange} value={formData.employee_count}>
            <SelectTrigger 
              className="bg-[#09090b] border-white/10 text-[#f5f5f5] focus:border-[#3b82f6] focus:ring-[#3b82f6]"
              data-testid="demo-form-employee-count"
            >
              <SelectValue placeholder="Sélectionnez une tranche" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a2e] border-white/10">
              <SelectItem value="1-10" className="text-[#f5f5f5] focus:bg-[#3b82f6]/20">1 - 10</SelectItem>
              <SelectItem value="11-50" className="text-[#f5f5f5] focus:bg-[#3b82f6]/20">11 - 50</SelectItem>
              <SelectItem value="51-200" className="text-[#f5f5f5] focus:bg-[#3b82f6]/20">51 - 200</SelectItem>
              <SelectItem value="201-500" className="text-[#f5f5f5] focus:bg-[#3b82f6]/20">201 - 500</SelectItem>
              <SelectItem value="500+" className="text-[#f5f5f5] focus:bg-[#3b82f6]/20">500+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white py-6 rounded-full font-semibold text-base transition-all glow-blue-hover disabled:opacity-50"
          data-testid="demo-form-submit"
        >
          {isSubmitting ? 'Envoi en cours...' : 'Demander ma démo'}
        </Button>

        <p className="text-center text-[#71717a] text-sm">
          Réponse sous 24h.
        </p>
      </form>
    </motion.div>
  );
};

export default DemoForm;
